import type { FunctionComponent } from 'react';
import { useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';

import { useTablePagination } from '../../../common/data-grids/common/use-table-pagination';
import type { LearningOpportunitySearchRow } from '../../../common/data-grids/learning-opportunities/LearningOpportunitiesDataGrid';
import { LearningOpportunitiesDataGrid } from '../../../common/data-grids/learning-opportunities/LearningOpportunitiesDataGrid';
import { useLearningOpportunitiesSearchColumns } from '../../../common/data-grids/learning-opportunities/UseLearningOpportunitiesSearchColumns';
import { useNavigationToEntity } from '../../../common/use-navigation-to-entity';
import { defaultPagination } from '../../../global-search/use-global-search-table-params';
import { useGlobalSearchLearningOpportunitiesQuery } from '../../../query-hooks/global-search/learning-opportunities';
import type { SortOption } from '../../talent-directory/sort-option';

type LearningOpportunitiesBySearchTermTableProps = {
    readonly searchTerm: string;
};

export const LearningOpportunitiesBySearchTermTable: FunctionComponent<LearningOpportunitiesBySearchTermTableProps> = ({
    searchTerm,
}) => {
    const { translate } = useContext(ApplicationContext);
    const { paginationModel, setPaginationModel } = useTablePagination();

    const { navigateToLearningOpportunityPage } = useNavigationToEntity();

    const [sortOptions, setSortOptions] = useState<readonly SortOption[]>([]);

    const { data, isFetching, isPending } = useGlobalSearchLearningOpportunitiesQuery({
        pagination: { page: paginationModel.page, size: paginationModel.pageSize },
        searchTerm,
        sortedFields: sortOptions,
    });

    const columns = useLearningOpportunitiesSearchColumns();

    const learningOpportunities = data?.data ?? [];
    const pagination = data?.pagination ?? defaultPagination;

    const noDataMessage = translate('pages.global-search.no-data.learning-opportunities');

    const rows: readonly LearningOpportunitySearchRow[] = learningOpportunities.map((learningOpportunity) => ({
        id: learningOpportunity.id,
        createdAt: learningOpportunity.createdAt,
        skills: learningOpportunity.skills,
        title: learningOpportunity.title,
    }));

    return (
        <div style={{ height: '70vh', width: '100%' }}>
            <LearningOpportunitiesDataGrid
                columns={columns}
                isFetching={isFetching}
                isPending={isPending}
                noDataMessage={noDataMessage}
                paginationModel={paginationModel}
                rowCount={pagination.count}
                rows={rows}
                sortOptions={sortOptions}
                onPaginationModelChange={setPaginationModel}
                onRowClick={navigateToLearningOpportunityPage}
                onSortOptionsChange={setSortOptions}
            />
        </div>
    );
};
